import { FormLabel, InputBase, Typography } from "@mui/material";
import { type FC, type PropsWithChildren } from "react";
import { type DropzoneOptions, type FileRejection, useDropzone } from "react-dropzone";
import { type FieldRenderProps } from "react-final-form";
import { useIntl } from "react-intl";

import { AboveFieldContainer, EmptyFieldContainer, ExistingFieldContainer, FormContainer, Wrapper } from "./FileUpload.sc";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface Props extends FieldRenderProps<any, HTMLInputElement> {
    label: string;
    required?: boolean;
    disabled?: boolean;
    info: string;
    dropzoneOptions?: DropzoneOptions;
}

const Dropzone: FC<PropsWithChildren<Props>> = ({ input, label, required = true, disabled = false, info, dropzoneOptions }) => {
    const defaultDropzoneOptions: DropzoneOptions = {
        multiple: false,
        onDrop: async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            acceptedFiles.forEach((file) => {
                if (input.onChange) {
                    input.onChange({ file });
                }
            });
        },
    };
    const { getRootProps, getInputProps } = useDropzone({
        ...defaultDropzoneOptions,
        ...dropzoneOptions,
    });

    const FieldContainer = input.value ? ExistingFieldContainer : EmptyFieldContainer;

    return (
        <FormContainer>
            <AboveFieldContainer>
                <FormLabel>
                    {label}
                    {required && "*"}
                </FormLabel>
            </AboveFieldContainer>
            <FieldContainer {...getRootProps({ className: "dropzone" })}>
                <input {...getInputProps()} />
                {input.value ? <InputBase value={input.value.name} disabled={disabled} /> : <Typography>{info}</Typography>}
            </FieldContainer>
        </FormContainer>
    );
};

const Field: FC<PropsWithChildren<Props>> = ({ input, ...rest }) => {
    const intl = useIntl();
    const defaultValues = {
        label: intl.formatMessage({ id: "defaultChooseFileText", defaultMessage: "Datei auswählen" }),
        info: intl.formatMessage({
            id: "defaultChooseFileInfoText",
            defaultMessage: "Ziehen Sie eine Datei hierher oder klicken Sie, um eine Datei auszuwählen",
        }),
    };
    const props = { ...defaultValues, ...rest };

    return (
        <Wrapper>
            <Dropzone input={input} {...props} />
        </Wrapper>
    );
};

export default Field;
